"use client";

import { Popover as RadixPopover } from "radix-ui";
import React, { CSSProperties } from "react";
import "./Popover.css";
import { useTheme } from "../ThemeProvider";
import { csx } from "../../utils/string";

export type PopoverSide = "top" | "right" | "bottom" | "left";
export type PopoverAlign = "start" | "center" | "end";

export interface PopoverProps {
  /**
   * The trigger element for the popover.
   */
  trigger: React.ReactNode;
  /**
   * The content of the popover.
   */
  children: React.ReactNode;
  /**
   * Constrain the width of the popover to the width of the trigger.
   */
  constrainWidth?: boolean;
  /**
   * Control open state (controlled mode).
   */
  open?: boolean;
  /**
   * Default open state (uncontrolled mode).
   */
  defaultOpen?: boolean;
  /**
   * Callback when open state changes.
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Popover side placement
   * {@link PopoverSide}
   */
  side?: PopoverSide;
  /**
   * Popover alignment
   * {@link PopoverAlign}
   */
  align?: PopoverAlign;
  /**
   * Show an arrow pointing to the trigger.
   */
  arrow?: boolean;
  /**
   * Test id for the popover content.
   */
  "data-testid"?: string;
}

/**
 * A popover component that handles positioning and supports controlled and uncontrolled modes.
 *
 * A popover displays interactive content in context with a trigger element.
 * A tooltip displays a description of an element on hover or focus.
 *
 * API:
 *  - {@link PopoverProps}
 */
export function Popover({
  trigger,
  children,
  open,
  defaultOpen,
  onOpenChange,
  side = "top",
  align = "center",
  arrow = false,
  constrainWidth = false,
  "data-testid": testId,
}: PopoverProps) {
  const { className: themeClass, style: themeStyle } = useTheme();

  return (
    <RadixPopover.Root
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
    >
      <RadixPopover.Trigger asChild>
        <div>{trigger}</div>
      </RadixPopover.Trigger>

      <RadixPopover.Portal>
        <RadixPopover.Content
          side={side}
          sideOffset={2}
          align={align}
          style={themeStyle as CSSProperties}
          className={csx(
            "proton-Popover",
            constrainWidth && "proton-Popover__constrained",
            themeClass
          )}
          data-testid={testId}
        >
          {arrow && <RadixPopover.Arrow className="arrow" />}
          {children}
        </RadixPopover.Content>
      </RadixPopover.Portal>
    </RadixPopover.Root>
  );
}

Popover.displayName = "Popover";
